"use client";
import React from "react";
import { cn } from "@/lib/utils";
import Image from "next/image";
import StarGreenIcon from "@/assets/images/star_green.svg";
import LogoIcon from "@/assets/images/logo.svg";
import Link from "next/link";
import { toast } from "sonner";

import { useRouter } from "next/navigation";
import SearchBar from "@/components/dashboard/SearchBar";
import type { AddressData } from "@/types/address";
import {
	createCheckoutSessionWithAuth,
	STRIPE_PRO_PRICE_ID,
} from "@/services/stripe-api";
import SampleSearchResultsPreview from "@/components/sections/landing/SampleSearchResultsPreview";

interface FooterProps {
	className?: string;
	showSearch?: boolean;
}

const Footer: React.FC<FooterProps> = ({ className, showSearch = true }) => {
	const router = useRouter();
	const [isLoading, setIsLoading] = React.useState(false);
	const [isUpgrading, setIsUpgrading] = React.useState(false);

	const handleAddressSelect = (address: AddressData) => {
		if (address.latitude && address.longitude) {
			setIsLoading(true);
			const params = new URLSearchParams();
			params.set("lat", address.latitude.toString());
			params.set("lng", address.longitude.toString());
			params.set("address", encodeURIComponent(address.formattedAddress));
			router.push(`/playground?${params.toString()}`);
		}
	};

	const handleUpgrade = async () => {
		setIsUpgrading(true);
		try {
			const { url } =
				await createCheckoutSessionWithAuth(STRIPE_PRO_PRICE_ID);
			window.location.href = url;
		} catch (error) {
			console.error("Failed to start checkout:", error);
			// Don't show error if redirecting to login
			if (
				error instanceof Error &&
				error.message !== "Redirecting to login"
			) {
				toast.error("Failed to start checkout. Please try again.");
			}
			setIsUpgrading(false);
		}
	};

	return (
		<footer className={cn("w-full bg-brand-dark", className)}>
			{/* CTA Section */}
			{showSearch ? (
				<div className="w-full gradient-card py-24 overflow-visible">
					<div
						className="max-w-[90rem] mx-auto px-[7.5rem] text-center overflow-visible"
						style={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						{/* Title */}
						<h2 className="text-5xl font-semibold leading-[3.5rem] tracking-wide mb-12 font-bricolage mx-auto">
							<span className="text-white">
								See Inside the Platform – Access{" "}
							</span>
							<span className="text-brand-green">
								Professional Property
							</span>
							<span className="text-white">
								{" "}
								Intelligence Today
							</span>
						</h2>

						{/* Search Bar */}
						<div
							className="max-w-[49.5rem] mx-auto mb-6 overflow-visible"
							style={{ width: "100%" }}
						>
							<div className="relative z-[1000] overflow-visible">
								<SearchBar
									onAddressSelect={handleAddressSelect}
									isLoading={isLoading}
									fullWidth={true}
									inputClassName="!h-[3.5rem] !text-xl !px-12 !pr-16"
								/>
							</div>
						</div>

						<div className="mb-6 w-full max-w-[64rem] text-left">
							<SampleSearchResultsPreview
								compact
								title="Properties in New Castle"
								description="Real properties currently available in New Castle, Delaware."
							/>
						</div>

						{/* Promo Text */}
						<button
							onClick={handleUpgrade}
							disabled={isUpgrading}
							className="flex items-center justify-center gap-1 text-sm bg-transparent border-none p-0 cursor-pointer hover:opacity-80 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
							type="button"
						>
							<Image
								src={StarGreenIcon}
								alt="Tax Lien Vest Feature Logo"
							/>
							<span className="text-brand-green-light font-semibold tracking-wide">
								Try Free for 14 days •{" "}
							</span>
							<span className="text-brand-border font-semibold tracking-wide">
								No Credit Card Required • Cancel Anytime •
								Priority Support
							</span>
							<Image
								src={StarGreenIcon}
								alt="Tax Lien Vest Feature Logo"
							/>
						</button>
					</div>
				</div>
			) : null}

			{/* Footer Content */}
			<div className="max-w-[90rem] mx-auto px-[7.5rem] py-10">
				<div className="flex flex-col gap-10">
					{/* Top Row */}
					<div className="flex items-center justify-between">
						{/* Logo */}
						<div className="flex items-center gap-2">
							<Image
								src={LogoIcon}
								alt="Tax Lien Vest Logo"
								width={38}
								height={38}
								className="w-[2.375rem] h-[2.375rem]"
							/>
							<span className="text-white text-xl font-semibold tracking-wide font-bricolage">
								Tax Lien Vest
							</span>
						</div>

						{/* Links */}
						<nav
							className="flex items-center gap-10"
							data-testid="footer-nav"
						>
							<div data-testid="footer-about-link">
								<Link
									href="/about"
									className="text-white text-base font-medium hover:text-brand-green transition-colors"
								>
									About
								</Link>
							</div>
							<div data-testid="footer-privacy-policy-link">
								<Link
									href="/privacy-policy"
									className="text-white text-base font-medium hover:text-brand-green transition-colors"
								>
									Privacy Policy
								</Link>
							</div>
							<div data-testid="footer-terms-conditions-link">
								<Link
									href="/terms-of-service"
									className="text-white text-base font-medium hover:text-brand-green transition-colors"
								>
									Terms & Conditions
								</Link>
							</div>
							<div data-testid="footer-contact-link">
								<Link
									href="/contact-us"
									className="text-white text-base font-medium hover:text-brand-green transition-colors"
								>
									Contact
								</Link>
							</div>
						</nav>
					</div>

					{/* Bottom Row */}
					<div className="pt-8 border-t border-brand-green-dark flex items-center justify-between">
						{/* Copyright */}
						<p className="text-brand-border text-base font-normal leading-6">
							© 2025 Lein-Vest. All rights reserved.
						</p>
					</div>
				</div>
			</div>
		</footer>
	);
};

export default Footer;
